/**
 * The warehouse location endpoints, in one place.
 *
 * Every screen that puts an item away, moves one, or asks what is in a
 * location goes through here — the camera putaway modal, the handheld
 * screens, and anything that comes later. Keeping the request bodies in a
 * single module is what stops the two clients drifting apart on the details
 * that matter: which fields are omitted rather than sent null, and where the
 * backend-allocated slot number actually lives on the response.
 *
 * All of it proxies to the Python warehouse authority via the worker at
 * workers/src/routes/inventoryLocations.js, which enforces the `warehouse`
 * permission and attaches the operator identity headers.
 */

import { api } from './api'

const BASE = '/api/inventory-locations'

// A location can hold more than the default page of items; 200 comfortably
// covers the largest tote and matches what Stock Check asks for.
const CONTENTS_LIMIT = 200

// What "in the tote" means when someone is looking at the physical contents.
const IN_STOCK = 'in_stock'

/**
 * The slot to request for a given shelf type.
 *
 * `large_bay` returns null so the body omits `position` and the backend
 * allocates the next free number — multi-item bays then get honest sequential
 * slots instead of everything piling onto "1". Everything else records as "L"
 * until a real L/R picker exists.
 */
export function defaultPositionFor(shelfType) {
    return shelfType === 'large_bay' ? null : 'L'
}

/** Metadata for one shelf or tote. Throws ApiError 404 when Prism has no such location. */
export function lookupShelf(shelfId) {
    return api.get(`${BASE}/shelves/${encodeURIComponent(shelfId)}`)
}

/**
 * Items physically in one location, with titles and images for display.
 *
 * Restricted to stock that is actually sellable. A location row survives the
 * sale of the item sitting on it, so an unfiltered read lists things that left
 * the building weeks ago, and an operator checking a tote against the screen
 * is then hunting for stock that does not exist.
 */
export function shelfContents(shelfId) {
    return searchItems({ shelf_id: shelfId, status: IN_STOCK, limit: CONTENTS_LIMIT })
}

/** Inventory items matching any combination of q / status / shelf_id / has_location. */
export function searchItems(params = {}) {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null || value === '') continue
        query.set(key, String(value))
    }
    const suffix = query.toString()
    return api.get(suffix ? `${BASE}/items?${suffix}` : `${BASE}/items`)
}

/**
 * Read one item back by its inventory id, or null if it is not found.
 *
 * The `q` filter also matches on title, so the exact id has to be re-checked
 * here: a search for an id can drag in unrelated products that merely mention
 * it in their name.
 */
export async function fetchItem(itemId) {
    const response = await searchItems({ q: itemId, limit: 5 })
    const rows = Array.isArray(response) ? response : response?.items ?? []
    return rows.find((row) => row.item_id === itemId) ?? null
}

/**
 * Place an item into a location for the first time.
 * @returns {Promise<{shelf_id: string, position: string|null}>} The slot as recorded.
 */
export async function commitPutaway({ itemId, shelfId, position = null }) {
    const body = { item_id: itemId, shelf_id: shelfId }
    if (position != null) body.position = position

    const response = await api.post(`${BASE}/putaway/queue`, body)
    return { shelf_id: shelfId, position: response?.position ?? position ?? null }
}

/**
 * Move an item between two locations.
 *
 * `scannedCurrentMatchesSystem` is the operator's word that the item really
 * was where Prism thought it was. Passing false still performs the move —
 * the operator is treated as the source of truth — but the backend records an
 * `inventory_correction` audit event alongside it. Leave it out entirely when
 * the caller never checked: the camera flow moves an item without confirming
 * where it started, and should not claim otherwise just because the backend
 * would have defaulted the field to true anyway.
 *
 * @returns {Promise<{shelf_id: string, position: string|null}>} The slot as recorded.
 */
export async function commitMove({
    itemId,
    fromShelf,
    fromPosition,
    toShelf,
    toPosition = null,
    reason,
    scannedCurrentMatchesSystem
}) {
    const body = {
        item_id: itemId,
        from_shelf: fromShelf,
        to_shelf: toShelf,
        reason
    }
    if (scannedCurrentMatchesSystem != null) {
        body.scanned_current_matches_system = scannedCurrentMatchesSystem
    }
    // from_position is required and must be a non-empty string. Omit the field
    // rather than sending null when the row has no recorded slot.
    if (fromPosition != null) body.from_position = fromPosition
    // to_position is optional: omitting it lets the backend allocate the slot.
    if (toPosition != null) body.to_position = toPosition

    const response = await api.post(`${BASE}/move`, body)
    // /move returns the destination ItemLocationRead, so the new slot is on
    // `position` — not `to_position`.
    return { shelf_id: toShelf, position: response?.position ?? toPosition ?? null }
}
